import React, { useCallback, useEffect, useRef } from 'react';

import { App, Store, StoreBase } from '@flipdish/api-client-typescript';
import { FormControlLabel, makeStyles, Paper, Theme } from '@material-ui/core';
import { getTranslate, Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { RouteComponentProps, useHistory } from 'react-router';

import { EnableSwitch, Typography } from '@fd/ui/atoms';
import { FormSection } from '@fd/ui/molecules';

import * as deliveryZonesActions from '../../../actions/deliveryzones.actions';
import { storeActions } from '../../../actions/store.actions';
import * as storeConstants from '../../../constants/store.constants';
import { appMapCenterSelector } from '../../../selectors/app.selector';
import {
  createLoadingErrorSelectorFactory,
  createLoadingSelector,
} from '../../../selectors/loading.selector';
import { createStoreMapCenterSelector, getSelectedStore } from '../../../selectors/store.selector';
import { createDeliveryZonesSelector } from '../../../selectors/storeDeliveryZone.selector';
import { createStoreGroupSelectorById } from '../../../selectors/storegroup.selector';
import { StoreEvents } from '../../../signalr/hub.actions';
import PageLayout from '../../../ui/Layout';
import DeliveryZones from '../../DeliveryZones/DeliveryZones';

const initialLoadingSelector = createLoadingSelector([storeConstants.STORE_LOAD]);
const initialLoadingErrorSelector = createLoadingErrorSelectorFactory([storeConstants.STORE_LOAD]);

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    paddingBottom: theme.spacing(4),
  },
  description: {
    whiteSpace: 'pre-line',
  },
  descriptionContainer: {
    marginBottom: theme.spacing(3),
  },
  switch: {
    display: 'flex',
    justifyContent: 'space-between',
    marginLeft: 0,
    maxWidth: '70%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
    },
  },
}));

type DeliverySettingsProps = RouteComponentProps<{ storeId; appId; storeGroupId }> &
  MappedState &
  MappedDispatch;

const DeliverySettings = (props: DeliverySettingsProps) => {
  const classes = useStyles();
  const {
    add,
    canEdit,
    deliveryZones,
    dispose,
    initLoad,
    isLoading,
    isSmallScreen,
    loadAll,
    loadDependencies,
    match,
    onChecked,
    remove,
    select,
    store,
    storeGroupCurrency,
    storeMapCenter,
    storeName,
    toggleEvents,
    translate,
    update,
    updateStore,
    zones,
    location,
  } = props;

  const isMounted = useRef(false);
  const history = useHistory();

  useEffect(() => {
    loadAll(
      match.params.appId,
      Number(props.match.params.storeGroupId),
      Number(props.match.params.storeId),
      loadDependencies
    );
    toggleEvents(true);
    return () => {
      toggleEvents(false);
      dispose();
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      const newUrl = match.url.replace(/storegroups\/.*/, 'storegroups');
      history.replace(newUrl, location.state);
    } else {
      isMounted.current = true;
    }
  }, [match.params.appId]);

  const backToParent = useCallback(({ history }) => {
    const parent = history.location.pathname.replace('/deliverysettings', '');
    history.push(parent);
  }, []);

  const disableFormFields = deliveryZones?.length === 0 || !deliveryZones;

  const pageLoading = isLoading || !initLoad;
  return (
    <PageLayout
      toParent={backToParent}
      caption={(store && store.Name) || ''}
      documentTitle="Delivery_settings_title"
      title={<Translate id="Delivery_settings_title" />}
    >
      <>
        <DeliveryZones
          add={add}
          initLoad={initLoad}
          isSmallScreen={isSmallScreen}
          match={match}
          remove={remove}
          select={select}
          storeGroupCurrency={storeGroupCurrency}
          storeMapCenter={storeMapCenter}
          storeName={storeName}
          update={update}
          zones={zones}
        />
        <Paper className={classes.container}>
          <FormSection
            isLoading={pageLoading}
            sectionTitle={translate('Require_customer_name') as string}
          >
            <div className={classes.descriptionContainer}>
              <Typography className={classes.description} variant="caption">
                {translate('Customer_name_requirements_description')}
              </Typography>
            </div>
            <FormControlLabel
              label={undefined}
              className={classes.switch}
              control={
                <EnableSwitch
                  name="RequireCustomerNameForDelivery"
                  checked={store ? store.RequireCustomerNameForDelivery : false}
                  onChange={(e) => store && onChecked(e, store, updateStore)}
                  fdKey="require_customer_name_for_delivery_orders"
                  disabled={!canEdit || disableFormFields}
                />
              }
            />
          </FormSection>
          <FormSection
            isLoading={pageLoading}
            sectionTitle={translate('Accept_cash_for_delivery_orders') as string}
          >
            <FormControlLabel
              label={undefined}
              className={classes.switch}
              control={
                <EnableSwitch
                  name="CashOrdersDeliveryEnabled"
                  checked={store ? store.CashOrdersDeliveryEnabled : false}
                  onChange={(e) => store && onChecked(e, store, updateStore)}
                  fdKey="cash_orders_delivery_enabled"
                  disabled={!canEdit || disableFormFields}
                />
              }
            />
          </FormSection>
          <FormSection
            isLoading={pageLoading}
            sectionTitle={translate('SMS_messaging') as string}
            showDivider={false}
          >
            <div className={classes.descriptionContainer}>
              <Typography className={classes.description} variant="caption">
                {translate('Include_estimated_time_in_the_SMS')}
              </Typography>
            </div>
            <FormControlLabel
              label={undefined}
              className={classes.switch}
              control={
                <EnableSwitch
                  name="EtaInDeliveryConfirmationSmsEnabled"
                  checked={store ? store.EtaInDeliveryConfirmationSmsEnabled : false}
                  onChange={(e) => store && onChecked(e, store, updateStore)}
                  fdKey="include_estimated_delivery_time_in_sms"
                  disabled={!canEdit || disableFormFields}
                />
              }
            />
          </FormSection>
        </Paper>
      </>
    </PageLayout>
  );
};

type updateStore = (storeId: number, store: StoreBase) => (dispatch: any) => void;

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch) => {
  const deliveryZoneUpdateAction = deliveryZonesActions.update(true);

  return {
    loadAll: (appId, storeGroupId, storeId, loadDependencies = false) => {
      dispatch(deliveryZonesActions.loadAll(appId, storeGroupId, storeId, loadDependencies));
    },
    add: (storeId, zone, tmpId) => {
      dispatch(deliveryZonesActions.create(storeId, zone, tmpId));
    },
    update: (storeId, zone, undo) => {
      dispatch(deliveryZoneUpdateAction(storeId, zone.Id, zone, undo));
    },
    remove: (storeId, storeDeliveryZoneId, undo) => {
      dispatch(deliveryZonesActions.remove(storeId, storeDeliveryZoneId, undo));
    },
    select: (deliveryZoneId: number) => {
      dispatch(deliveryZonesActions.select(deliveryZoneId));
    },
    toggleEvents: (toggle: boolean) => {
      if (toggle) {
        dispatch(StoreEvents.Subscribe.DeliveryZoneCreated);
        dispatch(StoreEvents.Subscribe.DeliveryZoneUpdated);
        dispatch(StoreEvents.Subscribe.DeliveryZoneDeleted);
      } else {
        dispatch(StoreEvents.Unsubscribe.DeliveryZoneCreated);
        dispatch(StoreEvents.Unsubscribe.DeliveryZoneUpdated);
        dispatch(StoreEvents.Unsubscribe.DeliveryZoneDeleted);
      }
    },
    dispose: () => {
      dispatch(deliveryZonesActions.reset());
    },
    onChecked: (
      event: React.ChangeEvent<HTMLInputElement>,
      store: Store,
      updateStore: updateStore
    ) => {
      store &&
        store.StoreId &&
        dispatch(
          updateStore(store.StoreId, {
            ...store,

            [event.target.name]:
              event.target.name === 'AutomaticallyAcceptOrders'
                ? !event.target.checked
                : event.target.checked,
          })
        );
    },
  };
};

type MappedState = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState, ownProps) => {
  const updateFunction = storeActions.update(true);
  const canEdit = state.permissions.some((p) => p === App.AppResourceSetEnum.EditStores.toString());
  const { storeGroupId, storeId } = ownProps.match.params;
  const storeGroupSelectorById = createStoreGroupSelectorById(storeGroupId);
  const zoneSelector = createDeliveryZonesSelector(storeId);
  const storeMapCenterSelector = createStoreMapCenterSelector(storeId);
  const storeGroup = storeGroupSelectorById(state);
  const store = getSelectedStore(state);
  const zones = zoneSelector(state);
  const storeMapCenter = storeMapCenterSelector(state) || appMapCenterSelector(state);

  return {
    canEdit,
    isLoading: initialLoadingSelector(state),
    loadingError: initialLoadingErrorSelector(state),
    selectedApp: state.currentApp,
    store: getSelectedStore(state),
    translate: getTranslate(state.locale),
    updateStore: updateFunction,
    deliveryZones: state.orm?.StoreDeliveryZone?.items,
    zones,
    initLoad: state.deliveryZones.initLoad,
    storeGroupCurrency: storeGroup && store ? storeGroup.Currency : null,
    loadDependencies: !(storeGroup && store),
    storeMapCenter,
    storeName: store ? store.Name : '',
    isSmallScreen: ownProps.width === 'xs',
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(DeliverySettings);
